import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Section,
  Heading,
  Paragraph,
  Link,
  Loading,
  IconStatusInformationOnlight,
  FormLayout,
  OrderedList,
  HorizontalRule,
  ModalDialog,
  ButtonRow,
  Button,
  ListItem
} from '@piggybank/core';
import {
  Fieldset,
  Form,
  IconTile,
  TileGroup,
  Field,
  Checkbox,
  FieldFeedback,
} from '@piggybank/form';
import { WithI18nProgressIndicator } from 'components/WithI18nPiggybank';
import { ButtonWithBack } from 'components/ButtonWithBack';
import TncPdfViewer from './TncPdfViewer';
import { declaration as declarationList } from 'referenceData';
import { CHINA, PASSPORT, EEP } from 'referenceData/constants';
import { userFetch, rePrevious } from 'utils/util';
import { checkBrowser } from 'utils/browser';
import { TNC_REVIEW_PDF_URLS } from 'utils/tncPdf';
import { getUserSessionDataByKey, setUserSessionDataByKey } from 'storage/userStorage';
import './custom.css';
import email from 'assets/image/email.svg';
import call from 'assets/image/call.svg';
import SMS from 'assets/image/SMS.png';
import { useTagging, linkTagging, globalViewTaggingParams, loadTagging } from 'tealiumTrack';

const iconSet = [
  SMS,
  email,
  call,
];

const TAG_PAGE_URL = '/forms/declaration';
const TAG_PAGE_NAME = 'pws:forms:declaration';
const CHANNEL_VALUES = declarationList.map(item => item.value);
const CHANNEL_EMAIL = 'Email';
const RECOMMEND_CHANNEL_VALUE = [CHANNEL_EMAIL];
const ADDITIONAL_DECLARATION_ITEM_IDS = ['1', '2', '3', '4', '5', '6'];
const ADDITIONAL_DECLARATION_NOTE_IDS = ['TITLE', '1', '2', '3', '4'];
const ADDITIONAL_DECLARATION_SPACED_NOTE_IDS = ['2', '3'];
const ADDITIONAL_DECLARATION_LINK_NOTE_ID = '2';
const TNC_SCROLL_THRESHOLD = 8;

export const shouldShowAdditionalDeclaration = (personalValues) => personalValues?.nationality === CHINA
  && [PASSPORT, EEP].includes(personalValues?.documentType);

export const getTncReviewCheckboxWrapperClass = (hasReachedTncEnd, hasTncError) => [
  'tnc-review-checkbox-wrapper',
  hasReachedTncEnd ? '' : 'tnc-review-checkbox-wrapper-disabled',
  hasTncError ? 'tnc-review-checkbox-wrapper-error' : '',
].filter(Boolean).join(' ');

export const hasReachedTncReviewEnd = (reviewWindow, lastPage) => {
  if (!reviewWindow || !lastPage || !lastPage.classList.contains('tnc-pdfjs-page-rendered')) {
    return false;
  }

  const reviewWindowRect = reviewWindow.getBoundingClientRect();
  const lastPageRect = lastPage.getBoundingClientRect();

  return lastPageRect.bottom <= reviewWindowRect.bottom + TNC_SCROLL_THRESHOLD
    && lastPageRect.bottom >= reviewWindowRect.top + TNC_SCROLL_THRESHOLD;
};

export const getPdfOpenTarget = (browser) => {
  if (!browser?.isUCBrowser && !browser?.isQQBrowser && !browser?.isIEBrowser) {
    return undefined;
  }

  return browser.isIEBrowser ? '_blank' : '_self';
};

export const renderAdditionalDeclarationNote = (noteId, text, t) => {
  if (noteId === ADDITIONAL_DECLARATION_LINK_NOTE_ID) {
    const noteLinkText = t('ICO.DECLARATION.ADDITIONAL_DECLARATION_NOTE_2_LINK');

    return (
      <>
        {text}
        <span className='additional-declaration-note-link-text'>
          {noteLinkText}
        </span>
      </>
    );
  }

  if (noteId !== '4') {
    return text;
  }

  const matchedSeparator = text.includes('：') ? '：' : ':';
  const separatorIndex = text.indexOf(matchedSeparator);

  if (separatorIndex === -1) {
    return text;
  }

  const label = text.slice(0, separatorIndex + 1);
  const content = text.slice(separatorIndex + 1);

  return (
    <>
      <span className='font-weight-medium'>{label}</span>
      {content}
    </>
  );
}

const Declaration = () => {
  const initialValues = getUserSessionDataByKey('declaration');
  const personalValues = getUserSessionDataByKey('personal');
  const { isPremier, enableCreditCard, lang } = getUserSessionDataByKey('global');
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const hasInitiallyReachedTncEnd = Boolean(initialValues?.ackTncIndicator || initialValues?.termsConditionsConsent);
  const [hasReachedTncEnd, setHasReachedTncEnd] = useState(hasInitiallyReachedTncEnd);
  const [isTncPdfRendered, setIsTncPdfRendered] = useState(process.env.NODE_ENV === 'test');
  const tncReviewWindowRef = useRef(null);
  const tncReviewLastPageRef = useRef(null);
  const hasReachedTncEndRef = useRef(hasInitiallyReachedTncEnd);
  const [hasTncSubmitAttempted, setHasTncSubmitAttempted] = useState(false);
  const hasTncSubmitAttemptedRef = useRef(false);
  const validateTncFieldRef = useRef(null);
  const setTncFieldErrorRef = useRef(null);
  const tncFieldErrorRef = useRef();
  const tncReviewPdfUrl = TNC_REVIEW_PDF_URLS[lang] || TNC_REVIEW_PDF_URLS.en;
  const declarationTncPdfUrl = t('ICO.DECLARATION.PDF1');
  const browser = checkBrowser();
  const showAdditionalDeclaration = shouldShowAdditionalDeclaration(personalValues);
  const schema = yup.object().shape({
    ackTncIndicator: yup
      .boolean()
      .test('tnc-review-acknowledgement-required', () => t(hasReachedTncEndRef.current
        ? 'ICO.DECLARATION.TNC_REVIEW_CHECKBOX_ERROR'
        : 'ICO.DECLARATION.TNC_REVIEW_SCROLL_ERROR'), (value) => {
        if (!hasTncSubmitAttemptedRef.current) {
          return true;
        }

        return value === true;
      }),
    additionalDeclarationConsent: yup
      .boolean()
      .test('additional-declaration-consent-required', () => t('ICO.CREDIT.LTA_CHECKBOX_ERROR'), (value) => {
        if (!showAdditionalDeclaration) {
          return true;
        }

        return value === true;
      })
  });
  const history = useHistory();

  useEffect(() => {
    if (!hasReachedTncEnd || !tncFieldErrorRef.current) {
      return;
    }

    if (setTncFieldErrorRef.current) {
      setTncFieldErrorRef.current('ackTncIndicator', t('ICO.DECLARATION.TNC_REVIEW_CHECKBOX_ERROR'));
    } else if (validateTncFieldRef.current) {
      validateTncFieldRef.current('ackTncIndicator');
    }
  }, [hasReachedTncEnd, t]);

  const markTncReachedEnd = () => {
    if (hasReachedTncEndRef.current) {
      return;
    }

    hasReachedTncEndRef.current = true;
    setHasReachedTncEnd(true);

    if (tncFieldErrorRef.current && setTncFieldErrorRef.current) {
      setTncFieldErrorRef.current('ackTncIndicator', t('ICO.DECLARATION.TNC_REVIEW_CHECKBOX_ERROR'));
    } else if (tncFieldErrorRef.current && validateTncFieldRef.current) {
      Promise.resolve().then(() => validateTncFieldRef.current('ackTncIndicator'));
    }
  };

  const updateTncReachedEnd = () => {
    const reviewWindow = tncReviewWindowRef.current;
    const lastPage = tncReviewLastPageRef.current;

    if (!reviewWindow || hasReachedTncEndRef.current) {
      return;
    }

    if (hasReachedTncReviewEnd(reviewWindow, lastPage)) {
      markTncReachedEnd();
    }
  };

  const handleTncRenderComplete = () => {
    setIsTncPdfRendered(true);
    updateTncReachedEnd();
  };

  const openPDF = (event) => {
    event.preventDefault();

    const openTarget = getPdfOpenTarget(browser);

    if (openTarget) {
      globalThis.open?.(declarationTncPdfUrl, openTarget);
    } else {
      globalThis.open?.(declarationTncPdfUrl);
    }
  };

  const clickLoading = () => {
    setLoading(true);
    loadTagging({
      ...globalViewTaggingParams,
      'page_url': '/forms/processing',
      'page_language': lang,
      'page_name': 'pws:forms:processing',
      'page_subcategory': 'processing',
      'raw_datalayer': '5745v27',
      'funnel_name': `account opening: ${isPremier ? 'premier' : 'one'}`,
      'funnel_step': '8',
      'funnel_step_name': 'processing',
      'application_event': 'submitted'
    });
    userFetch()
      .then(() => {
        setLoading(false);
        history.push('/happens');
      }).catch(() => {
        setLoading(false);
        history.push('/technicals');
      })
  }

  const closePopup = () => {
    setShowModal(false);
  };

  const popupContinueBtn = (setFieldValue) => {
    setFieldValue('preferredChannels', RECOMMEND_CHANNEL_VALUE);
    handleOverallCheckbox(setFieldValue, RECOMMEND_CHANNEL_VALUE);
    setShowModal(false);
  };

  const popupSkipToSubmit = (values) => {
    setLoading(true);
    setUserSessionDataByKey('declaration', values);
    clickLoading();
    setShowModal(false);
    linkTagging({
      'page_url': TAG_PAGE_URL,
      'page_language': lang,
      'event_category': 'content',
      'event_action': 'checkbox',
      'event_content': 'I prefer not to receive marketing or promotional materials',
      'page_name': TAG_PAGE_NAME,
      'raw_datalayer': '5745v24'
    });
  };

  const handleOverallCheckbox = (setFieldValue, channel = []) => {
    setFieldValue('agreeAllTerms', !!channel.length);
  };

  const markTncSubmitAttempted = () => {
    hasTncSubmitAttemptedRef.current = true;
    setHasTncSubmitAttempted(true);
  };

  const handleTncScroll = () => {
    if (hasReachedTncEnd) {
      return;
    }

    updateTncReachedEnd();
  };

  const scrollTncReviewToBottom = () => {
    const reviewWindow = tncReviewWindowRef.current;

    if (reviewWindow) {
      reviewWindow.scrollIntoView({ block: 'start' });
      reviewWindow.scrollTop = reviewWindow.scrollHeight - reviewWindow.clientHeight;
    }

    updateTncReachedEnd();
  };

  useTagging({
    'page_url': TAG_PAGE_URL,
    'page_language': lang,
    'page_name': TAG_PAGE_NAME,
    'page_subcategory': 'declaration',
    'raw_datalayer': '5745v22',
    'funnel_name': `account opening: ${isPremier ? 'premier' : 'one'}`,
    'funnel_step': '7',
    'funnel_step_name': 'declaration'
  });
  return (
    <>
      <Loading show={loading} getApplicationNode={() => document.getElementById('root')} >
        <Heading level={3}>
          {t('ICO.HAPPENS.PROCESSING')}
        </Heading>
        <Paragraph marginBottom={0}>
          {t('ICO.HAPPENS.BACK')}
        </Paragraph>
      </Loading>
      <Form
        initialValues={{
          ackTncIndicator: initialValues?.ackTncIndicator ?? initialValues?.termsConditionsConsent ?? false,
          additionalDeclarationConsent: false,
          ...initialValues
        }}
        validationSchema={schema}
        onSubmit={({ values }) => {
          if (values.preferredChannels.length > 0) {
            setUserSessionDataByKey('declaration', values);
            clickLoading();
          } else {
            setShowModal(true);
          }
        }}
        className="declaration-form"
        onChange={rePrevious}
      >
        {({ values, setFieldValue, errors, validateField, setFieldError }) => {
          validateTncFieldRef.current = validateField;
          setTncFieldErrorRef.current = setFieldError;
          tncFieldErrorRef.current = errors?.ackTncIndicator;

          return (
            <FormLayout sizeMedium={10} sizeLarge={8}>
              <WithI18nProgressIndicator current={isPremier && enableCreditCard ? 7 : 6} />
              <Section marginBottom={4}>
                <Heading level={2} accentBar>
                  {t('ICO.DECLARATION.DIRECT')}
                </Heading>
                <Paragraph marginBottom={3}>{t('ICO.DECLARATION.HOW')}</Paragraph>
                <HorizontalRule hiddenOnSmallViewport marginBottom={4} aria-hidden='true' />
                {/* Choose channel section */}
                <Heading level={3} marginBottom={3}>{t('ICO.DECLARATION.PROMOTIONAL_OFFERS_TITLE')}</Heading>
                <Field name="agreeAllTerms" marginBottom={4}
                  onChange={(next, { value, ...rest }) => {
                    setFieldValue('preferredChannels', value ? CHANNEL_VALUES : []);
                    next({ value, ...rest });
                  }}
                >
                  <Checkbox>
                    <span className="font-weight-bold">{t('ICO.DECLARATION.CHOOSE_LIKE_RECEIVE_OFFERS')}</span> 
                    <Paragraph hint marginBottom={2}>
                      <span>{t('ICO.DECLARATION.LEARN_MORE_DATA')}</span>
                      <Link
                        buttonPadding={false}
                        elementType="a"
                        external
                        href={t('ICO.DECLARATION.LIKE_RECEIVE_OFFERS_HINT_LINK')}
                        strong={false}
                        textMap={{ newWindow: 'Opens in a new window' }}
                      >
                        {t('ICO.DECLARATION.LEARN_MORE_DATA_LINK_CONTENT')}
                      </Link>
                      <span>{t('ICO.DECLARATION.LEARN_MORE_DATA_SUFFIX')}</span>
                    </Paragraph>
                  </Checkbox>
                </Field>
                <Fieldset
                    name='preferredChannels'
                    marginBottom={4}
                    onChange={(next, { value, ...rest }) => {
                      handleOverallCheckbox(setFieldValue, value);
                      linkTagging({
                        'page_url': TAG_PAGE_URL,
                        'page_language': lang,
                        'event_category': 'content',
                        'event_action': 'checkbox',
                        'event_content': `direct marketing: ${value.join(',')}`,
                        'page_name': TAG_PAGE_NAME,
                        'raw_datalayer': '5745v23'
                      });
                      next({ value, ...rest });
                    }}
                  >
                  <TileGroup multiSelect>
                    {declarationList.map((item, index) => (
                      <IconTile fullWidth key={item.value} value={item.value} icon={() => {
                        return <img alt='' src={iconSet[index]} style={{ width: 20, height: 20 }} />
                      }}>
                        {item[lang]}
                      </IconTile>
                    ))}
                  </TileGroup>
                </Fieldset>
              </Section>
              <Section>
                <Paragraph marginBottom={2}>{t('ICO.DECLARATION.BANKEARLIER')}</Paragraph>
                <Paragraph marginBottom={2}>{t('ICO.DECLARATION.MARKETING')}</Paragraph>
                <Paragraph hint>{t('ICO.DECLARATION.MARKETING_TIPS')}</Paragraph>
              </Section>
              <Section marginBottom={4}>
                <Heading level={1}>
                  {t('ICO.DECLARATION.DECLARATIONS')}
                </Heading>
                <Paragraph className='Declaration-left'>
                  {t('ICO.DECLARATION.DECLARE')}
                </Paragraph>
                <OrderedList>
                  {/***1***/}
                  <ListItem>
                    {t('ICO.DECLARATION.DECLAREALLLISTA')}
                    {isPremier ? t('ICO.TYPE.PREMIER_TITLE') : t('ICO.TYPE.ONE_TITLE')}
                    {t('ICO.DECLARATION.DECLAREALLLISTA1')}
                  </ListItem>
                  {/***2***/}
                  <ListItem>
                    {t('ICO.DECLARATION.DECLAREALLLISTB')}
                  </ListItem>
                  {/***3***/}
                  <ListItem>
                    {t('ICO.DECLARATION.DECLAREALLLISTC')}
                    <Link
                      buttonPadding={false}
                      elementType="a"
                      external
                      href={declarationTncPdfUrl}
                      strong={false}
                      textMap={{
                        newWindow: ''
                      }}
                      onClick={(event) => {
                        openPDF(event);
                        linkTagging({
                          'page_url': TAG_PAGE_URL,
                          'page_language': lang,
                          'event_category': 'content',
                          'event_action': 'onsite',
                          'event_content': 'terms and conditions',
                          'page_name': TAG_PAGE_NAME,
                          'raw_datalayer': '5745v25'
                        });
                      }}
                    >
                      {t('ICO.DECLARATION.DECLAREALLLISTCLIKE')}
                    </Link>
                    <div className='tnc-review-section'>
                      <section
                        id='tnc-review-window'
                        ref={tncReviewWindowRef}
                        className='tnc-review-window'
                        data-testid='tnc-review-window'
                        onScroll={handleTncScroll}
                        aria-label={t('ICO.DECLARATION.TNC_REVIEW_WINDOW_LABEL')}
                      >
                        <TncPdfViewer
                          pdfUrl={tncReviewPdfUrl}
                          lastPageRef={tncReviewLastPageRef}
                          onRenderComplete={handleTncRenderComplete}
                        />
                      </section>
                      <div className='tnc-review-go-bottom-row'>
                        <Button type="button" disabled={!isTncPdfRendered} onClick={scrollTncReviewToBottom}>
                          {t('ICO.DECLARATION.TNC_REVIEW_GO_TO_BOTTOM')}
                        </Button>
                      </div>
                      <div className={getTncReviewCheckboxWrapperClass(hasReachedTncEnd, hasTncSubmitAttempted && errors?.ackTncIndicator)}>
                        <Field name="ackTncIndicator" marginBottom={0}>
                          <Checkbox disabled={!hasReachedTncEnd}>
                            {t('ICO.DECLARATION.TNC_REVIEW_ACKNOWLEDGEMENT')}
                          </Checkbox>
                          <FieldFeedback />
                        </Field>
                      </div>
                    </div>
                  </ListItem>
                  {/***Premier 4 only***/}
                  {isPremier && <ListItem>
                    {t('ICO.DECLARATION.DATA')}
                    <Link
                      buttonPadding={false}
                      elementType="a"
                      external
                      href={t('ICO.DECLARATION.PDF3')}
                      strong={false}
                      textMap={{
                        newWindow: ''
                      }}
                    >
                      {t('ICO.DECLARATION.DATALIKE')}
                    </Link>
                    {t('ICO.DECLARATION.DATA9')}
                    <Link
                      buttonPadding={false}
                      elementType="a"
                      external
                      href={t('ICO.DECLARATION.PDF4')}
                      strong={false}
                      textMap={{
                        newWindow: ''
                      }}
                    >
                      {t('ICO.DECLARATION.DATA2')}
                    </Link>
                    {t('ICO.DECLARATION.DATA3')}
                    <Link
                      buttonPadding={false}
                      elementType="a"
                      external
                      href={t('ICO.DECLARATION.PDF5')}
                      strong={false}
                      textMap={{
                        newWindow: ''
                      }}
                    >
                      {t('ICO.DECLARATION.DATA4')}
                    </Link>
                    {t('ICO.DECLARATION.DATA5')}
                  </ListItem>
                  }
                  {/***One 4, Premier 5***/}
                  <ListItem>
                    <Paragraph>{t('ICO.DECLARATION.DECLARE_O4P5')}</Paragraph>
                    {t('ICO.DECLARATION.CHANNELS')}
                    <OrderedList type="lower-alpha">
                      <ListItem>{t('ICO.DECLARATION.CHANNELS1')}</ListItem>
                      <ListItem>{t('ICO.DECLARATION.CHANNELS2')}</ListItem>
                      <ListItem>{t('ICO.DECLARATION.CHANNELS3')}</ListItem>
                      <ListItem>{t('ICO.DECLARATION.CHANNELS4')}</ListItem>
                    </OrderedList>
                    {t('ICO.DECLARATION.CHANNELS_REMIND')}<Link
                      buttonPadding={false}
                      elementType="a"
                      external
                      strong={false}
                      textMap={{ newWindow: '' }}
                      href={'https://' + t('ICO.DECLARATION.CHANNELS_REMIND_LINK')
                      }>{t('ICO.DECLARATION.CHANNELS_REMIND_LINK')}</Link>{lang === 'en' ? '.' : '。'}
                  </ListItem>
                  {/***One 5, Premier 6***/}
                  <ListItem>
                    {t('ICO.DECLARATION.DATA6')}
                    <Link
                      buttonPadding={false}
                      elementType="a"
                      external
                      href={t('ICO.DECLARATION.PDF2')}
                      strong={false}
                      textMap={{
                        newWindow: ''
                      }}
                      onClick={() => {
                        linkTagging({
                          'page_url': TAG_PAGE_URL,
                          'page_language': lang,
                          'event_category': 'content',
                          'event_action': 'onsite',
                          'event_content': 'data privacy notice',
                          'page_name': TAG_PAGE_NAME,
                          'raw_datalayer': '5745v26'
                        });
                      }}
                    >
                      {t('ICO.DECLARATION.DATA7')}
                    </Link>
                    {t('ICO.DECLARATION.DATA8')}
                  </ListItem>
                </OrderedList>
                <Paragraph style={{ display: 'flex' }}>
                  <span
                    style={{
                      color: 'var(--sapphire)',
                      marginTop: '3px'
                    }}
                  >
                    <IconStatusInformationOnlight />
                  </span>
                  <span style={{ marginLeft: '7px' }}>{t('ICO.DECLARATION.MAKESURE')}</span>
                </Paragraph>
                {showAdditionalDeclaration && (
                  <>
                    <div className='additional-declaration-section'>
                      <Heading level={3} marginBottom={3}>
                        {t('ICO.DECLARATION.ADDITIONAL_DECLARATION_TITLE')}
                      </Heading>
                      <Paragraph className='additional-declaration-intro'>
                        {t('ICO.DECLARATION.ADDITIONAL_DECLARATION_INTRO')}
                      </Paragraph>
                      <OrderedList className='additional-declaration-list'>
                        {ADDITIONAL_DECLARATION_ITEM_IDS.map((itemId) => (
                          <ListItem key={itemId}>
                            <span className="font-weight-bold">{t(`ICO.DECLARATION.ADDITIONAL_DECLARATION_ITEM_${itemId}_TITLE`)}</span>
                            <Paragraph marginBottom={0}>{t(`ICO.DECLARATION.ADDITIONAL_DECLARATION_ITEM_${itemId}_CONTENT`)}</Paragraph>
                          </ListItem>
                        ))}
                      </OrderedList>
                      <div className='additional-declaration-notes'>
                        {ADDITIONAL_DECLARATION_NOTE_IDS.map((noteId, index) => (
                          <Paragraph
                            key={noteId}
                            className={[
                              noteId === 'TITLE' ? 'font-weight-medium' : '',
                              ADDITIONAL_DECLARATION_SPACED_NOTE_IDS.includes(noteId) ? 'additional-declaration-note-spacing' : ''
                            ].filter(Boolean).join(' ') || undefined}
                            marginBottom={
  index === ADDITIONAL_DECLARATION_NOTE_IDS.length - 1 ? 0 : 2
}>
                            {renderAdditionalDeclarationNote(
                              noteId,
                              t(`ICO.DECLARATION.ADDITIONAL_DECLARATION_NOTE_${noteId}`),
                              t
                            )}
                          </Paragraph>
                        ))}
                      </div>
                      <Field name="additionalDeclarationConsent" marginBottom={0}>
                        <Checkbox>{t('ICO.DECLARATION.ADDITIONAL_DECLARATION_NOTE_5')}</Checkbox>
                        <FieldFeedback />
                      </Field>
                    </div>
                  </>
                )}
              </Section>
              <ButtonWithBack
                isSubmit={true}
                onClick={markTncSubmitAttempted} />
              <ModalDialog
                getApplicationNode={function () { return document.getElementById('root'); }}
                show={showModal}
                textMap={{
                  closeButtonLabel: t('ICO.FINANCIAL.MODAL_ICON_CLOSE')
                }}
                onClose={closePopup}
                title={t('ICO.DECLARATION.CONSENT_POPUP_TITLE')}
              >
                <Paragraph marginBottom={6}>{t('ICO.DECLARATION.CONSENT_POPUP_SUBTITLE')}</Paragraph>
                <HorizontalRule hiddenOnSmallViewport />
                <ButtonRow>
                  <Button onClick={() => { popupContinueBtn(setFieldValue) }}>{t('ICO.DECLARATION.CONSENT_POPUP_OK_BUTTON')}</Button>
                  <Button secondary onClick={() => { popupSkipToSubmit(values) }}>{t('ICO.DECLARATION.CONSENT_POPUP_SKIP_BUTTON')}</Button>
                </ButtonRow>
              </ModalDialog>
            </FormLayout>
          )
        }}
      </Form >
    </>
  );
}

export default Declaration;
